/**
 * Global Network Gate - Prevents request storms during network outages
 * 
 * When network errors are detected, this gate enters a cooldown period.
 * During cooldown, ALL polling/heartbeat calls should skip making requests.
 * 
 * Features:
 * - Detects network errors (ERR_NETWORK, ERR_CONNECTION_REFUSED, etc.)
 * - Exponential backoff (30s initial, max 2 minutes)
 * - Tab visibility awareness (pauses when tab hidden)
 * - Automatic resume when network recovers
 * 
 * IMPORTANT: This gate does NOT change endpoint routing or business logic.
 * It only prevents unnecessary repeated calls during downtime.
 */

interface NetworkGateState {
  isDown: boolean;
  cooldownUntil: number;
  failCount: number;
  lastFailureTime: number;
  lastFailureReason?: string;
  lastFailureKey?: string;
}

// Global state
let gateState: NetworkGateState = {
  isDown: false,
  cooldownUntil: 0,
  failCount: 0,
  lastFailureTime: 0,
};

// Tab visibility state
let isTabVisible = typeof document !== 'undefined' ? !document.hidden : true;

// Listen for tab visibility changes
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    isTabVisible = !document.hidden;
  });
}

const INITIAL_COOLDOWN_MS = 30000; // 30 seconds
const MAX_COOLDOWN_MS = 120000; // 2 minutes
const BACKOFF_MULTIPLIER = 1.5; // Exponential backoff factor

/**
 * Check if network is currently down (in cooldown)
 */
export function isNetworkDown(): boolean {
  if (!isTabVisible) {
    return true; // Treat hidden tab as "down" to pause polling
  }
  
  const now = Date.now();
  if (gateState.isDown && now < gateState.cooldownUntil) {
    return true;
  }
  
  // Cooldown expired - reset state
  if (gateState.isDown && now >= gateState.cooldownUntil) {
    gateState.isDown = false;
    gateState.failCount = 0;
    gateState.cooldownUntil = 0;
  }
  
  return false;
}

/**
 * Mark network as down and enter cooldown
 * @param ms Cooldown duration in milliseconds (default: 30s, with exponential backoff)
 * @param meta Optional metadata about the failure
 */
export function markNetworkDown(
  ms: number = INITIAL_COOLDOWN_MS,
  meta?: { key?: string; reason?: string }
): void {
  const now = Date.now();
  
  // Calculate cooldown with exponential backoff
  const backoffMs = gateState.failCount > 0
    ? Math.min(MAX_COOLDOWN_MS, ms * Math.pow(BACKOFF_MULTIPLIER, gateState.failCount))
    : ms;
  
  gateState.isDown = true;
  gateState.cooldownUntil = now + backoffMs;
  gateState.failCount += 1;
  gateState.lastFailureTime = now;
  gateState.lastFailureReason = meta?.reason;
  gateState.lastFailureKey = meta?.key;
}

/**
 * Check if network requests should be skipped
 * Returns true if:
 * - Network is down (in cooldown)
 * - Tab is hidden
 */
export function shouldSkipNetwork(): boolean {
  return isNetworkDown() || !isTabVisible;
}

/**
 * Mark network as recovered (call when a request succeeds)
 */
export function markNetworkUp(): void {
  if (gateState.isDown) {
    gateState.isDown = false;
    gateState.failCount = 0;
    gateState.cooldownUntil = 0;
    gateState.lastFailureTime = 0;
    gateState.lastFailureReason = undefined;
    gateState.lastFailureKey = undefined;
  }
}

/**
 * Check if an error is a network-type error
 * @param error Axios error or any error object
 */
export function isNetworkError(error: any): boolean {
  if (!error) return false;
  
  // Axios network errors
  if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED' || error.code === 'ERR_CONNECTION_REFUSED') {
    return true;
  }
  
  // Browser network errors
  if (error.message?.includes('ERR_CONNECTION_REFUSED') || 
      error.message?.includes('ERR_EMPTY_RESPONSE') ||
      error.message?.includes('Network Error')) {
    return true;
  }
  
  // No response = network error
  if (!error.response) {
    return true;
  }
  
  // Server errors that indicate downtime (optional - can be treated as network issues)
  const status = error.response?.status;
  if (status === 502 || status === 503 || status === 504) {
    return true;
  }
  
  return false;
}

/**
 * Get current network gate state (for debugging)
 */
export function getNetworkGateState(): Readonly<NetworkGateState & { isTabVisible: boolean }> {
  return {
    ...gateState,
    isTabVisible,
  };
}

/**
 * Reset network gate state (for testing)
 */
export function resetNetworkGate(): void {
  gateState = {
    isDown: false,
    cooldownUntil: 0,
    failCount: 0,
    lastFailureTime: 0,
  };
  isTabVisible = typeof document !== 'undefined' ? !document.hidden : true;
}

